"use client";

import {
	AlertTriangle,
	Check,
	Clock3,
	Download,
	FileVideo2,
	FolderOpen,
	Gauge,
	ImageIcon,
	Info,
	Maximize2,
	Play,
	RefreshCcw,
	Scissors,
	ShieldCheck,
	Sparkles,
	Trash2,
	UploadCloud,
	X,
} from "lucide-react";
import {
	type ChangeEvent,
	type DragEvent,
	useEffect,
	useRef,
	useState,
} from "react";
import { useLocale, useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils/ui";
import {
	loadFFmpeg,
	convertToGif,
	type ConversionProgress,
} from "@/lib/ffmpeg";

type ToolPhase =
	| "empty"
	| "inspecting"
	| "ready"
	| "unsupported"
	| "processing"
	| "success";
type PresetKey = "small" | "balanced" | "crisp";

interface Preset {
	width: number;
	fps: number;
}

const PRESETS: Record<PresetKey, Preset> = {
	small: { width: 320, fps: 8 },
	balanced: { width: 480, fps: 12 },
	crisp: { width: 640, fps: 18 },
};
const PRESET_KEYS: readonly PresetKey[] = ["small", "balanced", "crisp"];

function formatBytes(bytes: number, locale: string): string {
	if (bytes < 1024) return `${bytes} B`;
	const units = ["KB", "MB", "GB"];
	let value = bytes / 1024;
	let unit = units[0];
	for (let index = 1; value >= 1024 && index < units.length; index += 1) {
		value /= 1024;
		unit = units[index];
	}
	return `${new Intl.NumberFormat(locale, {
		maximumFractionDigits: value >= 10 ? 0 : 1,
	}).format(value)} ${unit}`;
}

function formatTime(seconds: number): string {
	if (!Number.isFinite(seconds)) return "0:00.0";
	const minutes = Math.floor(seconds / 60);
	const remainder = seconds - minutes * 60;
	return `${minutes}:${remainder.toFixed(1).padStart(4, "0")}`;
}

export function VideoToGif() {
	const t = useTranslations("VideoToGif.tool");
	const locale = useLocale();
	const inputRef = useRef<HTMLInputElement>(null);
	const videoRef = useRef<HTMLVideoElement>(null);
	const videoUrlRef = useRef<string | null>(null);
	const [file, setFile] = useState<File | null>(null);
	const [videoUrl, setVideoUrl] = useState<string | null>(null);
	const [phase, setPhase] = useState<ToolPhase>("empty");
	const [error, setError] = useState<string | null>(null);
	const [isDragging, setIsDragging] = useState(false);
	const [duration, setDuration] = useState(0);
	const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
	const [currentTime, setCurrentTime] = useState(0);
	const [startTime, setStartTime] = useState(0);
	const [endTime, setEndTime] = useState(0);
	const [preset, setPreset] = useState<PresetKey>("balanced");
	const [width, setWidth] = useState(PRESETS.balanced.width);
	const [fps, setFps] = useState(PRESETS.balanced.fps);
	const [loop, setLoop] = useState("Forever");
	const [isConverting, setIsConverting] = useState(false);
	const [conversionProgress, setConversionProgress] = useState(0);
	const [gifResult, setGifResult] = useState<{
		blob: Blob;
		url: string;
		size: number;
	} | null>(null);
	const [conversionError, setConversionError] = useState<string | null>(null);

	useEffect(
		() => () => {
			if (videoUrlRef.current) URL.revokeObjectURL(videoUrlRef.current);
		},
		[],
	);

	const resetFile = () => {
		if (videoUrlRef.current) URL.revokeObjectURL(videoUrlRef.current);
		videoUrlRef.current = null;
		setVideoUrl(null);
		setFile(null);
		setPhase("empty");
		setError(null);
		setDuration(0);
		setDimensions({ width: 0, height: 0 });
		setCurrentTime(0);
		setStartTime(0);
		setEndTime(0);
		setGifResult(null);
		setConversionError(null);
		setConversionProgress(0);
		if (inputRef.current) inputRef.current.value = "";
	};

	const handleConvert = async () => {
		if (!file || phase !== "ready") return;

		setIsConverting(true);
		setConversionProgress(0);
		setConversionError(null);
		setPhase("processing");

		try {
			// Load FFmpeg if not already loaded
			await loadFFmpeg();

			const clipDuration = endTime - startTime;

			// Build FFmpeg command options
			const options = {
				format: "gif",
				gifSize: width >= 640 ? "1080p" : width >= 480 ? "720p" : "480p",
				gifFrameRate: `${fps} fps`,
				loop,
			};

			// For trimming, we need to pass the start time and duration
			// We'll handle this in the FFmpeg args building
			(options as any).startTime = startTime;
			(options as any).duration = clipDuration;

			// Convert to GIF
			const gifBlob = await convertToGif({
				file,
				options,
				onProgress: (progress: ConversionProgress) => {
					setConversionProgress(progress.percent);
				},
			});

			// Create result
			const gifUrl = URL.createObjectURL(gifBlob);
			setGifResult({
				blob: gifBlob,
				url: gifUrl,
				size: gifBlob.size,
			});
			setPhase("success");
		} catch {
			setConversionError(t("errors.conversion"));
			setPhase("ready");
		} finally {
			setIsConverting(false);
		}
	};

	const downloadGif = () => {
		if (!gifResult || !file) return;

		const link = document.createElement("a");
		link.href = gifResult.url;
		const originalName = file.name.replace(/\.[^/.]+$/, "") || "video";
		link.download = `${originalName}-${Date.now()}.gif`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	const resetAndKeepFile = () => {
		if (gifResult?.url) {
			URL.revokeObjectURL(gifResult.url);
		}
		setGifResult(null);
		setConversionError(null);
		setConversionProgress(0);
		setPhase("ready");
	};

	const chooseFile = (nextFile?: File) => {
		if (!nextFile) return;
		setConversionError(null);
		setError(null);
		if (!nextFile.type.startsWith("video/")) {
			resetFile();
			setError(t("errors.invalidFile"));
			return;
		}
		setPhase("inspecting");
		if (videoUrlRef.current) URL.revokeObjectURL(videoUrlRef.current);
		const nextUrl = URL.createObjectURL(nextFile);
		videoUrlRef.current = nextUrl;
		setVideoUrl(nextUrl);
		setFile(nextFile);
	};

	const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
		chooseFile(event.target.files?.[0]);
	};

	const handleDrop = (event: DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		setIsDragging(false);
		chooseFile(event.dataTransfer.files[0]);
	};

	const handleMetadata = () => {
		const video = videoRef.current;
		if (!video || !Number.isFinite(video.duration) || video.duration <= 0) {
			setPhase("unsupported");
			setError(t("errors.metadata"));
			return;
		}
		setDuration(video.duration);
		setDimensions({ width: video.videoWidth, height: video.videoHeight });
		setStartTime(0);
		setEndTime(Math.min(video.duration, 5));
		setPhase("ready");
	};

	const handleVideoError = () => {
		setPhase("unsupported");
		setError(t("errors.preview"));
	};

	const applyPreset = (key: PresetKey) => {
		const nextPreset = PRESETS[key];
		setPreset(key);
		setWidth(nextPreset.width);
		setFps(nextPreset.fps);
		setConversionError(null);
	};

	const clipDuration = Math.max(0, endTime - startTime);
	const outputHeight = dimensions.width
		? Math.round((width * dimensions.height) / dimensions.width)
		: 0;
	const sizePressure = clipDuration * (width / 320) * (fps / 8);
	const risk =
		sizePressure > 34 ? "High" : sizePressure > 16 ? "Medium" : "Low";
	const riskStyles = {
		Low: "border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200",
		Medium:
			"border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200",
		High: "border-orange-200 bg-orange-50 text-orange-950 dark:border-orange-900 dark:bg-orange-950/40 dark:text-orange-200",
	};

	const setStartFromPlayhead = () => {
		setStartTime(Math.min(currentTime, Math.max(0, endTime - 0.1)));
		setConversionError(null);
	};

	const setEndFromPlayhead = () => {
		setEndTime(Math.max(currentTime, Math.min(duration, startTime + 0.1)));
		setConversionError(null);
	};

	return (
		<section
			aria-label={t("ariaLabel")}
			className="mx-auto w-full max-w-[84rem]"
		>
			<div className="overflow-hidden rounded-[2rem] border border-blue-200/80 bg-slate-50/90 shadow-[0_28px_80px_-42px_rgba(37,99,235,0.48)] backdrop-blur-xl dark:border-blue-900/70 dark:bg-slate-950/88 dark:shadow-[0_28px_80px_-42px_rgba(34,211,238,0.25)]">
				<div className="flex flex-col gap-3 border-b border-blue-100 bg-white/80 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-7 dark:border-blue-950 dark:bg-slate-950/80">
					<div>
						<h2 className="font-bold tracking-tight">{t("header.title")}</h2>
						<p className="text-muted-foreground mt-0.5 text-sm">
							{t("header.description")}
						</p>
					</div>
					<div className="flex flex-wrap items-center gap-2 text-xs font-semibold">
						<span className="inline-flex min-h-8 items-center gap-1.5 rounded-full border border-cyan-200 bg-cyan-50 px-3 text-cyan-950 dark:border-cyan-900 dark:bg-cyan-950/50 dark:text-cyan-100">
							<ShieldCheck className="size-3.5" aria-hidden="true" />{" "}
							{t("header.local")}
						</span>
						<span className="inline-flex min-h-8 items-center rounded-full border border-slate-200 bg-white px-3 text-slate-700 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200">
							{t("header.noAccount")}
						</span>
					</div>
				</div>

				<div className="p-4 sm:p-6 lg:p-8">
					{!file ? (
						<div
							className={cn(
								"relative flex min-h-80 flex-col items-center justify-center rounded-3xl border-2 border-dashed bg-white/90 px-6 text-center shadow-inner transition-colors dark:bg-slate-900/80",
								isDragging
									? "border-cyan-400 bg-cyan-50/80 dark:border-cyan-400 dark:bg-cyan-950/30"
									: "border-blue-200 hover:border-blue-400 dark:border-blue-900 dark:hover:border-cyan-600",
							)}
							onDragEnter={(event) => {
								event.preventDefault();
								setIsDragging(true);
							}}
							onDragOver={(event) => event.preventDefault()}
							onDragLeave={(event) => {
								if (
									!(event.relatedTarget instanceof Node) ||
									!event.currentTarget.contains(event.relatedTarget)
								) {
									setIsDragging(false);
								}
							}}
							onDrop={handleDrop}
						>
							<div className="flex size-16 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-400 text-white shadow-lg shadow-blue-500/20">
								<UploadCloud className="size-7" aria-hidden="true" />
							</div>
							<p className="mt-6 text-2xl font-bold tracking-tight">
								{t("drop.title")}
							</p>
							<p className="text-muted-foreground mt-2 text-sm">
								{t("drop.description")}
							</p>
							<Button
								className="mt-6 h-12 rounded-xl bg-blue-600 px-6 text-white shadow-lg shadow-blue-600/20 hover:bg-blue-500"
								onClick={() => inputRef.current?.click()}
							>
								<FolderOpen className="size-4.5" aria-hidden="true" />
								{t("drop.choose")}
							</Button>
							<input
								ref={inputRef}
								type="file"
								accept="video/*"
								className="sr-only"
								onChange={handleInputChange}
							/>
							<p className="mt-5 inline-flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
								<ShieldCheck
									className="size-3.5 text-cyan-600"
									aria-hidden="true"
								/>
								{t("drop.privacy")}
							</p>
						</div>
					) : (
						<div className="grid gap-6 lg:grid-cols-[minmax(0,1.08fr)_minmax(21rem,0.92fr)]">
							<div className="min-w-0 space-y-4">
								<div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-950 shadow-sm dark:border-slate-800">
									<div className="relative flex aspect-video items-center justify-center">
										{phase === "inspecting" ? (
											<div
												className="flex flex-col items-center gap-3 text-slate-300"
												role="status"
											>
												<RefreshCcw
													className="size-6 animate-spin motion-reduce:animate-none"
													aria-hidden="true"
												/>
												{t("reading")}
											</div>
										) : null}
										{videoUrl ? (
											/* User-selected videos do not provide a captions track to this local preview. */
											/* eslint-disable-next-line jsx-a11y/media-has-caption */
											<video
												ref={videoRef}
												src={videoUrl}
												controls
												preload="metadata"
												className={cn(
													"h-full w-full object-contain",
													phase === "inspecting" && "invisible",
												)}
												onLoadedMetadata={handleMetadata}
												onTimeUpdate={(event) =>
													setCurrentTime(event.currentTarget.currentTime)
												}
												onError={handleVideoError}
											/>
										) : null}
									</div>
									<div className="flex items-center justify-between gap-3 border-t border-slate-800 bg-slate-900 px-4 py-3 text-sm text-slate-200">
										<div className="min-w-0">
											<p className="truncate font-semibold">{file.name}</p>
											<p className="mt-0.5 text-xs text-slate-400">
												{formatBytes(file.size, locale)}
												{dimensions.width
													? ` · ${dimensions.width}×${dimensions.height}`
													: ""}
												{duration ? ` · ${formatTime(duration)}` : ""}
											</p>
										</div>
										<Button
											variant="ghost"
											size="icon"
											className="size-10 shrink-0 rounded-xl text-slate-200 hover:bg-slate-800"
											onClick={resetFile}
											aria-label={t("remove")}
										>
											<Trash2 className="size-4" aria-hidden="true" />
										</Button>
									</div>
								</div>

								{phase === "ready" ||
								phase === "processing" ||
								phase === "success" ? (
									<div className="rounded-2xl border border-blue-100 bg-white p-5 shadow-sm dark:border-blue-950 dark:bg-slate-900">
										<div className="flex items-center justify-between gap-3">
											<div>
												<p className="font-bold">{t("trim.title")}</p>
												<p className="text-muted-foreground mt-1 text-sm">
													{t("trim.selectedClip")} · {formatTime(clipDuration)}
												</p>
											</div>
											<Scissors
												className="size-5 text-cyan-600 dark:text-cyan-300"
												aria-hidden="true"
											/>
										</div>
										<div className="mt-5 grid gap-5 sm:grid-cols-2">
											<label className="text-sm font-semibold">
												<span className="flex items-center justify-between gap-3">
													{t("trim.start")}{" "}
													<span className="font-mono text-xs">
														{formatTime(startTime)}
													</span>
												</span>
												<input
													type="range"
													min={0}
													max={Math.max(0, endTime - 0.1)}
													step={0.1}
													value={Math.min(
														startTime,
														Math.max(0, endTime - 0.1),
													)}
													onChange={(event) => {
														setStartTime(Number(event.target.value));
														setConversionError(null);
													}}
													className="mt-3 w-full cursor-pointer accent-blue-600"
												/>
												<button
													type="button"
													className="mt-2 min-h-9 rounded-lg text-xs font-semibold text-blue-700 underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:text-cyan-300"
													onClick={setStartFromPlayhead}
												>
													{t("trim.usePlayhead", {
														time: formatTime(currentTime),
													})}
												</button>
											</label>
											<label className="text-sm font-semibold">
												<span className="flex items-center justify-between gap-3">
													{t("trim.end")}{" "}
													<span className="font-mono text-xs">
														{formatTime(endTime)}
													</span>
												</span>
												<input
													type="range"
													min={Math.min(duration, startTime + 0.1)}
													max={duration}
													step={0.1}
													value={endTime}
													onChange={(event) => {
														setEndTime(Number(event.target.value));
														setConversionError(null);
													}}
													className="mt-3 w-full cursor-pointer accent-cyan-500"
												/>
												<button
													type="button"
													className="mt-2 min-h-9 rounded-lg text-xs font-semibold text-blue-700 underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:text-cyan-300"
													onClick={setEndFromPlayhead}
												>
													{t("trim.usePlayhead", {
														time: formatTime(currentTime),
													})}
												</button>
											</label>
										</div>
									</div>
								) : null}
							</div>

							<div className="min-w-0 space-y-4">
								<div className="rounded-2xl border border-blue-100 bg-white p-5 shadow-sm dark:border-blue-950 dark:bg-slate-900">
									<div className="flex items-center justify-between">
										<p className="font-bold">{t("output.title")}</p>
										<Sparkles
											className="size-5 text-blue-600 dark:text-cyan-300"
											aria-hidden="true"
										/>
									</div>
									<div
										className="mt-4 grid grid-cols-3 gap-2"
										role="group"
										aria-label={t("output.presetLabel")}
									>
										{PRESET_KEYS.map((key) => (
											<button
												type="button"
												key={key}
												aria-pressed={preset === key}
												disabled={phase !== "ready" && phase !== "success"}
												onClick={() => applyPreset(key)}
												className={cn(
													"min-h-12 rounded-xl border px-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
													preset === key
														? "border-blue-500 bg-blue-50 text-blue-800 dark:border-cyan-500 dark:bg-cyan-950/40 dark:text-cyan-100"
														: "border-slate-200 bg-slate-50 hover:bg-slate-100 dark:border-slate-800 dark:bg-slate-950 dark:hover:bg-slate-800",
												)}
											>
												{t(`presets.${key}.label`)}
												{preset === key ? (
													<Check
														className="mx-auto mt-1 size-3.5"
														aria-hidden="true"
													/>
												) : null}
											</button>
										))}
									</div>
								</div>

								<div className="rounded-2xl border border-blue-100 bg-white p-5 shadow-sm dark:border-blue-950 dark:bg-slate-900">
									<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
										<label className="text-sm font-semibold">
											<span className="mb-2 flex items-center gap-2">
												<Maximize2
													className="size-4 text-cyan-600"
													aria-hidden="true"
												/>{" "}
												{t("output.width")}
											</span>
											<select
												value={width}
												disabled={phase !== "ready" && phase !== "success"}
												onChange={(event) => {
													setWidth(Number(event.target.value));
													setConversionError(null);
												}}
												className="bg-background h-11 w-full rounded-xl border px-3 outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
											>
												<option value={320}>320 px</option>
												<option value={480}>480 px</option>
												<option value={640}>640 px</option>
												<option value={800}>800 px</option>
											</select>
										</label>
										<label className="text-sm font-semibold">
											<span className="mb-2 flex items-center gap-2">
												<Gauge
													className="size-4 text-cyan-600"
													aria-hidden="true"
												/>{" "}
												{t("output.frameRate")}
											</span>
											<select
												value={fps}
												disabled={phase !== "ready" && phase !== "success"}
												onChange={(event) => {
													setFps(Number(event.target.value));
													setConversionError(null);
												}}
												className="bg-background h-11 w-full rounded-xl border px-3 outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
											>
												<option value={8}>
													8 fps · {t("frameRate.smaller")}
												</option>
												<option value={12}>
													12 fps · {t("frameRate.balanced")}
												</option>
												<option value={15}>
													15 fps · {t("frameRate.smooth")}
												</option>
												<option value={18}>
													18 fps · {t("frameRate.smoother")}
												</option>
												<option value={24}>
													24 fps · {t("frameRate.largest")}
												</option>
											</select>
										</label>
									</div>

									<div className="mt-4 grid grid-cols-3 gap-2 rounded-xl bg-slate-50 p-3 text-center text-xs dark:bg-slate-950">
										<div>
											<span className="text-muted-foreground block">
												{t("output.clip")}
											</span>
											<strong className="mt-1 block">
												{formatTime(clipDuration)}
											</strong>
										</div>
										<div>
											<span className="text-muted-foreground block">
												{t("output.output")}
											</span>
											<strong className="mt-1 block">
												{outputHeight ? `${width}×${outputHeight}` : "—"}
											</strong>
										</div>
										<div>
											<span className="text-muted-foreground block">
												{t("output.color")}
											</span>
											<strong className="mt-1 block">
												{t(`presets.${preset}.quality`)}
											</strong>
										</div>
									</div>

									<details className="group mt-4 border-t pt-4">
										<summary className="flex min-h-11 cursor-pointer list-none items-center justify-between rounded-lg text-sm font-semibold outline-none focus-visible:ring-2 focus-visible:ring-ring">
											{t("output.advanced")}
											<span className="text-muted-foreground text-lg transition-transform group-open:rotate-45 motion-reduce:transition-none">
												+
											</span>
										</summary>
										<label className="mt-3 block text-sm font-semibold">
											{t("output.loop")}
											<select
												value={loop}
												onChange={(event) => setLoop(event.target.value)}
												disabled={phase !== "ready" && phase !== "success"}
												className="bg-background mt-2 h-11 w-full rounded-xl border px-3 outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
											>
												<option value="Forever">
													{t("output.loopForever")}
												</option>
												<option value="Once">{t("output.loopOnce")}</option>
												<option value="3 times">
													{t("output.loopTimes3")}
												</option>
											</select>
										</label>
										<p className="text-muted-foreground mt-3 text-xs leading-5">
											{t("output.noAudio")}
										</p>
									</details>
								</div>

								{phase === "ready" ? (
									<div
										className={cn(
											"rounded-2xl border p-4 text-sm",
											riskStyles[risk],
										)}
										role={risk === "High" ? "alert" : "status"}
									>
										<div className="flex items-start gap-3">
											{risk === "High" ? (
												<AlertTriangle
													className="mt-0.5 size-4.5 shrink-0"
													aria-hidden="true"
												/>
											) : (
												<Info
													className="mt-0.5 size-4.5 shrink-0"
													aria-hidden="true"
												/>
											)}
											<div>
												<p className="font-bold">
													{t("risk.title", {
														risk: t(`risk.levels.${risk.toLowerCase()}`),
													})}
												</p>
												<p className="mt-1 leading-6">
													{risk === "High"
														? t("risk.high")
														: risk === "Medium"
															? t("risk.medium")
															: t("risk.low")}
												</p>
											</div>
										</div>
									</div>
								) : null}

								{error ? (
									<div
										className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-100"
										role="alert"
									>
										<div className="flex items-start justify-between gap-3">
											<p className="leading-6">{error}</p>
											<button
												type="button"
												onClick={() => setError(null)}
												className="rounded-lg p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
												aria-label={t("errors.dismiss")}
											>
												<X className="size-4" aria-hidden="true" />
											</button>
										</div>
										<Button
											variant="outline"
											className="mt-3 h-10 rounded-xl"
											onClick={() => inputRef.current?.click()}
										>
											{t("actions.chooseAnother")}
										</Button>
									</div>
								) : null}

								{conversionError ? (
									<div
										className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-100"
										role="alert"
									>
										<div className="flex items-start justify-between gap-3">
											<div className="flex-1">
												<p className="font-bold">
													{t("actions.conversionFailed")}
												</p>
												<p className="mt-1 leading-6">{conversionError}</p>
											</div>
											<button
												type="button"
												onClick={() => setConversionError(null)}
												className="rounded-lg p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
												aria-label={t("errors.dismiss")}
											>
												<X className="size-4" aria-hidden="true" />
											</button>
										</div>
									</div>
								) : null}

								{phase === "processing" ? (
									<div
										className="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-950 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-100"
										role="status"
										aria-live="polite"
									>
										<div className="flex items-center gap-3">
											<RefreshCcw
												className="size-5 animate-spin motion-reduce:animate-none"
												aria-hidden="true"
											/>
											<div className="flex-1">
												<p className="font-bold">{t("actions.generating")}</p>
												<p className="mt-1 text-xs">
													{conversionProgress > 0
														? t("actions.progress", {
																progress: conversionProgress,
															})
														: t("actions.initializing")}
												</p>
											</div>
										</div>
										<div className="mt-3 h-2 overflow-hidden rounded-full bg-blue-200 dark:bg-blue-900">
											<div
												className="h-full bg-blue-600 transition-all duration-300 dark:bg-cyan-500"
												style={{ width: `${conversionProgress}%` }}
											/>
										</div>
									</div>
								) : null}

								{gifResult ? (
									<div
										className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-900 dark:bg-emerald-950/40"
										role="status"
										aria-live="polite"
									>
										<div className="mb-3 flex items-center gap-2 text-emerald-900 dark:text-emerald-100">
											<Check className="size-5" aria-hidden="true" />
											<p className="font-bold">{t("actions.success")}</p>
										</div>
										<img
											src={gifResult.url}
											alt={t("actions.generatedAlt")}
											className="mb-3 w-full rounded-xl border border-emerald-200 bg-white dark:border-emerald-900 dark:bg-slate-900"
										/>
										<div className="mb-3 text-xs text-emerald-800 dark:text-emerald-200">
											<strong>{formatBytes(gifResult.size, locale)}</strong> ·{" "}
											{width}×
											{Math.round(
												(width * dimensions.height) / dimensions.width,
											)}{" "}
											· {fps} fps · {clipDuration.toFixed(1)}s
										</div>
										<div className="flex gap-2">
											<Button
												className="flex-1 h-11 rounded-xl bg-emerald-600 text-white hover:bg-emerald-500"
												onClick={downloadGif}
											>
												<Download className="size-4" aria-hidden="true" />
												{t("actions.download")}
											</Button>
											<Button
												variant="outline"
												className="h-11 rounded-xl"
												onClick={resetAndKeepFile}
											>
												{t("actions.adjustRetry")}
											</Button>
										</div>
									</div>
								) : null}

								<Button
									className="h-13 w-full rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 text-base text-white shadow-lg shadow-blue-600/20 hover:from-blue-500 hover:to-cyan-400 disabled:opacity-50"
									disabled={
										phase !== "ready" || clipDuration <= 0 || isConverting
									}
									onClick={handleConvert}
								>
									{isConverting ? (
										<>
											<RefreshCcw
												className="size-4.5 animate-spin motion-reduce:animate-none"
												aria-hidden="true"
											/>
											{conversionProgress > 0
												? `${conversionProgress}%`
												: t("actions.starting")}
										</>
									) : (
										<>
											<ImageIcon className="size-4.5" aria-hidden="true" />
											{t("actions.generate")}
										</>
									)}
								</Button>
								<input
									ref={inputRef}
									type="file"
									accept="video/*"
									className="sr-only"
									onChange={handleInputChange}
								/>
							</div>
						</div>
					)}

					{!file && error ? (
						<p
							className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-100"
							role="alert"
						>
							{error}
						</p>
					) : null}
				</div>
			</div>
			<div className="mt-4 grid gap-3 text-sm sm:grid-cols-3">
				<div className="flex min-h-14 items-center gap-3 rounded-2xl border bg-background/75 px-4 backdrop-blur-xl">
					<FileVideo2
						className="size-4.5 text-blue-600 dark:text-cyan-300"
						aria-hidden="true"
					/>{" "}
					{t("footer.oneVideo")}
				</div>
				<div className="flex min-h-14 items-center gap-3 rounded-2xl border bg-background/75 px-4 backdrop-blur-xl">
					<Clock3
						className="size-4.5 text-blue-600 dark:text-cyan-300"
						aria-hidden="true"
					/>{" "}
					{t("footer.shortLoops")}
				</div>
				<div className="flex min-h-14 items-center gap-3 rounded-2xl border bg-background/75 px-4 backdrop-blur-xl">
					<Play
						className="size-4.5 text-blue-600 dark:text-cyan-300"
						aria-hidden="true"
					/>{" "}
					{t("footer.preview")}
				</div>
			</div>
		</section>
	);
}
